import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {useFormContext, useWatch} from "react-hook-form";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {FormAutocomplete, FormDateTimePicker} from "../../common/form-fields-wrappers";
import {IExtensionManagementForm} from "../../models/models";
import { resetFields } from "../ExtensionManagement";
import {dmsTimeFramesState, extensionDaysState, extensionManagementRaFetchedDataState} from "../state";
import {useMutation} from "@tanstack/react-query";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fetchExtensionDays} from "../services/http.services";
import {BILLING_OWNER_TYPE, CREDIT_TYPE_LABELS, CREDIT_TYPE_LIST, LOCALES} from "../../config";
import {useHasScope} from "../../../../config/globalConfig";
import {NetworkScope} from "../../types";
import CustomLoader from "../../../../util/component/CustomLoader";
import dayjs, {Dayjs} from "dayjs";
import {formatCurrency, dayjsDateTimeStamp, getLocaleByLang} from "../../utils";
import {createSaveErrorsManagement} from "../services/utils";
import {ClockPickerView} from "@mui/x-date-pickers";
import {WEEK_DAYS_IND} from "../config";

const ExtensionInfo = () => {
    const {t} = useTranslation(['network', 'translation']);
    const hasScope = useHasScope;
    const hasScopeRaHQ = hasScope(NetworkScope.RaHQ);
    const appLanguage = useTranslation();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const raExtensionForm = useFormContext<IExtensionManagementForm>();

    const [accordion, setAccordion] = useState(true);
    const [billingOwnerList, setBillingOwnerList] = useState<any[]>([]);

    const emraFetchedDataState = useRecoilValue(extensionManagementRaFetchedDataState);
    const setExtensionDaysData = useSetRecoilState(extensionDaysState);
    const dmsTimeFrames = useRecoilValue<any>(dmsTimeFramesState);

    const [requestedDropoffDateTimeFieldValue, billingOwnerFieldValue, expectedDropoffDate, requestedDropoffDateTimeList ] =
        useWatch<IExtensionManagementForm,
            [
                'requestedDropoffDateTime',
                'billingOwner',
                'expectedDropoffDate',
                'requestedDropoffDateTimeList'
            ]>(
        {
            name: [
                'requestedDropoffDateTime',
                'billingOwner',
                'expectedDropoffDate',
                'requestedDropoffDateTimeList'
            ]
        });

    const isOpening = (date: dayjs.Dayjs): boolean => {
        const ind = date.get('d');
        return dmsTimeFrames.openings[WEEK_DAYS_IND[ind]] !== '';
    }

    const isSpecialDayOpening = (date: dayjs.Dayjs): boolean => {
        return !!dmsTimeFrames.specialDayOpenings
                    .find((o: any) => date.isSame(o.date, 'day') && o.timeframes !== '');
    }

    /**
     * Parse times
     * @param openingString
     */
    const parseRange = (openingString: string | null) => {
        if (!openingString || openingString === '') {
            return [];
        }

        let result: [number, number][] = [];
        const timeRangeList = openingString.split(';');
        timeRangeList.forEach(tr => {
            const [minTime, maxTime] = tr.split('-').map(t => {
                const [hour, minutes] = t.split(':');
                return Number(hour) + Number(minutes) / 100;
            });
            if (!Number.isNaN(minTime) && !Number.isNaN(maxTime))
                result.push([Number(minTime), Number(maxTime)]);
        });
        return result;
    }

    /**
     * Check if the single date should be disabled
     */
    const shouldDisableDate = useCallback((date: dayjs.Dayjs): boolean => {
        return !(!!dmsTimeFrames && (dmsTimeFrames.afterHours || dmsTimeFrames.keybox || isOpening(date) || isSpecialDayOpening(date)));
    }, [dmsTimeFrames]);

    /**
     * Check if a specific date is disabled
     * @param time - time to check
     * @param clockTime - hours | minutes
     * @param date - selected date
     */
    const isDisabledTime = (time: number, clockTime: ClockPickerView, date: dayjs.Dayjs | null): boolean => {
        if (!date || !dayjs(date).isValid()) {
            return true;
        }

        if (!!dmsTimeFrames && (dmsTimeFrames.afterHours || dmsTimeFrames.keybox)) {
            return false;
        }

        const specialDayOpenings = dmsTimeFrames.specialDayOpenings.find((o: any) => date.isSame(o.date, 'day'));
        if (!!specialDayOpenings) {
            const parseTimeframes = parseRange(specialDayOpenings.timeframes);

            if (clockTime === 'hours') {
                let outOfHourRange = true;
                parseTimeframes.forEach(so => {
                    const [minHour, maxHour] = so;
                    outOfHourRange &&= (time < Math.floor(minHour) || time > Math.floor(maxHour));
                });
                return outOfHourRange;
            }
            else if (clockTime === 'minutes') {
                let outOfMinuteRange = true;
                parseTimeframes.forEach(so => {
                    const [minHour, maxHour] = so;
                    // @ts-ignore
                    const currentTime = date.hour() + (time / 100);
                    outOfMinuteRange &&= (currentTime < minHour || currentTime > maxHour);
                });
                return outOfMinuteRange;
            }
            else {
                return false;
            }
        }

        const parsedOpenings: any = {};
        for(const [key, value] of Object.entries(dmsTimeFrames.openings)) {
            parsedOpenings[key] = parseRange(value as string);
        }

        const selectedDay = date.get('d');
        if (clockTime === 'hours') {
            let outOfHourRange = true;

            parsedOpenings[WEEK_DAYS_IND[selectedDay]]
                .forEach((so: any) => {
                    const [minHour, maxHour] = so;
                    outOfHourRange &&= (time < Math.floor(minHour) || time > Math.floor(maxHour));
                });
            return outOfHourRange;
        }
        else if (clockTime === 'minutes') {
            let outOfMinuteRange = true;
            parsedOpenings[WEEK_DAYS_IND[selectedDay]]
                .forEach((so: any) => {
                    const [minHour, maxHour] = so;
                    const currentTime = date.hour() + (time / 100);
                    outOfMinuteRange &&= (currentTime < minHour || currentTime > maxHour);
                });
            return outOfMinuteRange;
        }
        return false;
    }

    /**
     * Check if the single time should be disabled
     */
    const shouldDisableTime = useCallback((time: number, clockTime: ClockPickerView): boolean => {
        if (!!dmsTimeFrames && (dmsTimeFrames.afterHours || dmsTimeFrames.keybox)) {
            return false;
        }

        return isDisabledTime(time, clockTime, raExtensionForm.getValues('requestedDropoffDateTime'));
    }, [dmsTimeFrames]);

    const minExtendedDateTime = useMemo(() => {
        let dropoffDate = null;
        if (requestedDropoffDateTimeList.length > 0) {
            dropoffDate = requestedDropoffDateTimeList[requestedDropoffDateTimeList.length -1];
        } else if (!!expectedDropoffDate) {
            dropoffDate = expectedDropoffDate;
        }

        const now = dayjs().add(1, 'minutes');
        if (dropoffDate?.isBefore(now)) {
            return now;
        }

        return dropoffDate;
    }, [expectedDropoffDate, requestedDropoffDateTimeList]);

    const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
        return getLocaleByLang(appLanguage.i18n.language);
    }, [appLanguage.i18n.language]);

    const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
        e.stopPropagation();

        setAccordion(expanded);
    }

    const viewDefaultError = (mess: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(mess),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    /** Fetch Km & Days Extra */
    const extensionDaysMutation = useMutation(
        fetchExtensionDays,
        {
            onSuccess: (response: any) => {
                const creditTypeLabels: any = CREDIT_TYPE_LABELS;
                raExtensionForm.setValue('extensionPeriod', response.extraDays);
                raExtensionForm.setValue('renewedByExtension', response.renewedByExtension);
                raExtensionForm.setValue('creditType', CREDIT_TYPE_LIST.includes(response?.creditType) ? creditTypeLabels[response.creditType] : '');
                raExtensionForm.setValue('reservedPricing', response.reservedTariffs);
                raExtensionForm.setValue('extensionTimestamp', dayjsDateTimeStamp());
                raExtensionForm.setValue('rentalExtensionAmount', formatCurrency(locale as LOCALES, response.extensionRentalAmount));
                // LPP-21950 (T2.64septies): `postSaleAncillaryPeriodPreviewAmount` e' il delta di periodo degli
                // accessori aggiunti al banco dopo la firma. Per contratto e' di sola visualizzazione e non
                // concorre a extensionAncillaryAmount ne' a extensionTotalAmount: la somma la fa il front-end,
                // e la fa SOLO qui, nella resa. `response` non viene mutato, perche' extensionAncillaryAmount e'
                // il valore dispositivo che ExtensionManagement rimanda al backend (NTW lo persiste su
                // EXTENSION_ANCILLARY_AMOUNT): sommarlo la' trasformerebbe la preview in un addebito.
                // Il campo puo' mancare dal JSON (@JsonInclude(NON_NULL) lato sc-be) e vale 0.
                const postSaleAncillaryPreview = response.postSaleAncillaryPeriodPreviewAmount ?? 0;
                const hasAncillaryAmount = response.extensionAncillaryAmount !== null && response.extensionAncillaryAmount !== undefined;
                // Senza importo accessori e senza preview il campo resta vuoto come oggi (formatCurrency rende "").
                const ancillariesExtensionDisplayed = !hasAncillaryAmount && postSaleAncillaryPreview === 0
                    ? response.extensionAncillaryAmount
                    : (response.extensionAncillaryAmount ?? 0) + postSaleAncillaryPreview;
                raExtensionForm.setValue('ancillariesExtensionAmount', formatCurrency(locale as LOCALES, ancillariesExtensionDisplayed, response.currency));
                // LPP-21950 (D34): la preview concorre anche al totale mostrato del preventivo. Stessa regola
                // della voce accessori qui sopra: la somma sta SOLO nella resa e `response.extensionTotalAmount`
                // resta intatto, perche' e' il valore dispositivo che ExtensionManagement rimanda al backend
                // (NTW lo persiste su extensionInfo): sommarlo la' trasformerebbe la preview in un addebito.
                const hasTotalAmount = response.extensionTotalAmount !== null && response.extensionTotalAmount !== undefined;
                // Senza totale e senza preview il campo resta vuoto come oggi (formatCurrency rende "").
                const totalExtensionDisplayed = !hasTotalAmount && postSaleAncillaryPreview === 0
                    ? response.extensionTotalAmount
                    : (response.extensionTotalAmount ?? 0) + postSaleAncillaryPreview;
                raExtensionForm.setValue('totalExtensionAmount', formatCurrency(locale as LOCALES, totalExtensionDisplayed, response.currency));
                setExtensionDaysData(response);
            },
            onError: (error) => {
                viewDefaultError(createSaveErrorsManagement(error));
            }
        });

    const getExtensionDays = (data: any) => {
        // let expectedDropOffDate = '';
        // const numExts = data.requestedDropoffDateTimeList.length;
        // if (numExts > 0) {
        //     expectedDropOffDate = data?.requestedDropoffDateTimeList[numExts -1]?.toISOString() as string;
        // } else {
        //     expectedDropOffDate = data?.expectedDropoffDate?.toISOString() as string;
        // }

        let reqExtra: any = {
            effectiveReturnDate:    requestedDropoffDateTimeFieldValue?.toISOString(),
            idRentalAgreement:      emraFetchedDataState.idRentalAgreement,
            billingOwnerId:         billingOwnerFieldValue?.code
        };
        extensionDaysMutation.mutate(reqExtra as any);
    }

    useEffect(() => {
        if (!!requestedDropoffDateTimeFieldValue) {
            raExtensionForm.trigger('requestedDropoffDateTime');
        }
    }, [requestedDropoffDateTimeFieldValue]);

    useEffect(() => {
        if (!requestedDropoffDateTimeFieldValue?.isValid()) {
            // reset fields
            resetFields(raExtensionForm, false);
            return;
        }

        if (!billingOwnerFieldValue) {
            resetFields(raExtensionForm, false);
            return;
        }

        if (!billingOwnerFieldValue) {
            return;
        }

        raExtensionForm.handleSubmit(getExtensionDays)();
    }, [requestedDropoffDateTimeFieldValue, billingOwnerFieldValue]);

    const onChangeRequestedDropoffDateTime = (dateTimeValue: dayjs.Dayjs | null) => {

        if (!!dateTimeValue && dayjs(dateTimeValue).isValid()) {
            const checkHour = isDisabledTime(dateTimeValue.get('hour'), 'hours', raExtensionForm.getValues('requestedDropoffDateTime'));
            const checkMinute = isDisabledTime(dateTimeValue.get('minute'), 'minutes', raExtensionForm.getValues('requestedDropoffDateTime'));

            if (checkHour || checkMinute) {
                raExtensionForm.setValue('requestedDropoffDateTimeErrorStatus', true);
            } else {
                raExtensionForm.setValue('requestedDropoffDateTimeErrorStatus', false);
                raExtensionForm.trigger('requestedDropoffDateTime');
            }
        } else {
            raExtensionForm.setValue('requestedDropoffDateTimeErrorStatus', false);
        }
    };

    useEffect(() => {
        const options: any[] = [];

        // First Billing Owner
        if (!!emraFetchedDataState?.billingOwner?.billingOwnerType) {
            switch(emraFetchedDataState.billingOwner.billingOwnerType) {
                case BILLING_OWNER_TYPE.PERSON:
                    options.push({
                        code: emraFetchedDataState.billingOwner.id,
                        description: emraFetchedDataState.billingOwner.firstName + ' ' + emraFetchedDataState.billingOwner.lastName
                    });
                    break;
                case BILLING_OWNER_TYPE.COMPANY:
                    if (!!emraFetchedDataState.financialInfo?.businessAgreement) {
                        if (hasScopeRaHQ) {
                            options.push({
                                code: emraFetchedDataState.billingOwner.id,
                                description: emraFetchedDataState.billingOwner.companyName
                            });
                        }
                    } else {
                        options.push({
                            code: emraFetchedDataState.billingOwner.id,
                            description: emraFetchedDataState.billingOwner.companyName
                        });
                    }
                    break;
                case BILLING_OWNER_TYPE.VAT_NUMBER:
                    options.push({
                        code: emraFetchedDataState.billingOwner.id,
                        description: emraFetchedDataState.billingOwner.companyName
                    });
                    break;
            }
        }
        else if (!!emraFetchedDataState?.billingOwner) {
            if (!!emraFetchedDataState.billingOwner?.companyName) {
                if (!!emraFetchedDataState.financialInfo?.businessAgreement) {
                    if (hasScopeRaHQ) {
                        options.push({
                            code: emraFetchedDataState.billingOwner.id,
                            description: emraFetchedDataState.billingOwner.companyName
                        });
                    }
                } else {
                    options.push({
                        code: emraFetchedDataState.billingOwner.id,
                        description: emraFetchedDataState.billingOwner.companyName
                    });
                }
            }
            else if (!!emraFetchedDataState.billingOwner?.vatNumber) {
                options.push({
                    code: emraFetchedDataState.billingOwner.id,
                    description: emraFetchedDataState.billingOwner.companyName
                });
            }
            else if (!!emraFetchedDataState.billingOwner?.firstName && !!emraFetchedDataState.billingOwner?.lastName) {
                options.push({
                    code: emraFetchedDataState.billingOwner.id,
                    description: emraFetchedDataState.billingOwner.firstName + ' ' + emraFetchedDataState.billingOwner.lastName
                });
            }
        }

        // Second Billing Owner
        if (!!emraFetchedDataState?.secondBillingOwner?.billingOwnerType) {
            switch (emraFetchedDataState.secondBillingOwner.billingOwnerType) {
                case BILLING_OWNER_TYPE.PERSON:
                    options.push({
                        code: emraFetchedDataState.secondBillingOwner.id,
                        description: emraFetchedDataState.secondBillingOwner.firstName + ' ' + emraFetchedDataState.secondBillingOwner.lastName
                    });
                    break;
                case BILLING_OWNER_TYPE.COMPANY:
                    if (!!emraFetchedDataState.financialInfo?.businessAgreement) {
                        if (hasScopeRaHQ) {
                            options.push({
                                code: emraFetchedDataState.secondBillingOwner.id,
                                description: emraFetchedDataState.secondBillingOwner.companyName
                            });
                        }
                    } else {
                        options.push({
                            code: emraFetchedDataState.secondBillingOwner.id,
                            description: emraFetchedDataState.secondBillingOwner.companyName
                        });
                    }
                    break;
                case BILLING_OWNER_TYPE.VAT_NUMBER:
                    options.push({
                        code: emraFetchedDataState.secondBillingOwner.id,
                        description: emraFetchedDataState.secondBillingOwner.companyName
                    });
                    break;
            }
        }
        else if (!!emraFetchedDataState?.secondBillingOwner) {
            if (!!emraFetchedDataState.secondBillingOwner?.companyName) {
                if (!!emraFetchedDataState.financialInfo?.businessAgreement) {
                    if (hasScopeRaHQ) {
                        options.push({
                            code: emraFetchedDataState.secondBillingOwner.id,
                            description: emraFetchedDataState.secondBillingOwner.companyName
                        });
                    }
                } else {
                    options.push({
                        code: emraFetchedDataState.secondBillingOwner.id,
                        description: emraFetchedDataState.secondBillingOwner.companyName
                    });
                }
            }
            else if (!!emraFetchedDataState.secondBillingOwner?.vatNumber) {
                options.push({
                    code: emraFetchedDataState.secondBillingOwner.id,
                    description: emraFetchedDataState.secondBillingOwner.companyName
                });
            }
            else if (!!emraFetchedDataState.secondBillingOwner?.firstName && !!emraFetchedDataState.secondBillingOwner?.lastName) {
                options.push({
                    code: emraFetchedDataState.secondBillingOwner.id,
                    description: emraFetchedDataState.secondBillingOwner.firstName + ' ' + emraFetchedDataState.secondBillingOwner.lastName
                });
            }
        }

        setBillingOwnerList(options);
    }, [emraFetchedDataState]);

    return (
        <>
            <Accordion expanded={accordion} onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant={"h6"}>
                        {t("extensionManagement.extensionManagementInfo.accordion.title")}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-start"}}>
                            <FormDateTimePicker<IExtensionManagementForm>
                                name="requestedDropoffDateTime"
                                control={raExtensionForm.control}
                                label={t("extensionManagement.extensionManagementInfo.form.label.requestedExtensionDropoffTime")}
                                minDateTime={minExtendedDateTime as Dayjs}
                                shouldDisableDate={shouldDisableDate}
                                shouldDisableTime={shouldDisableTime}
                                onChange={(value) => onChangeRequestedDropoffDateTime(value)}
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-start"}}>
                            <FormAutocomplete<IExtensionManagementForm>
                                name='billingOwner'
                                control={raExtensionForm.control}
                                label={t("extensionManagement.extensionManagementInfo.form.label.billingOwner")}
                                fullWidth
                                options={billingOwnerList}
                                isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                getOptionLabel={(opt: any) => opt?.description}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <CustomLoader loading={extensionDaysMutation.isLoading}/>
        </>

    );
}

export default ExtensionInfo;